<template>
  <div class="archives-tab">
      <div class="filters">
        <div>
          <h4>{{ $t("app.company.archives.listTitle") }}</h4>
          <p>{{ $t("app.company.archives.listDesc") }}</p>
        </div>
        <VField class="light sm white-background" :placeholder="$t('app.company.archives.search')" name="search" icon="bi-search" v-model="state.search.value" @input="(event) => (state.search.value = event.target.value)" />
      </div>
      <div class="archives-table">
        <Table v-if="!isPending" :data="archivedCampaigns.entries" :fields="state.fields" :search="state.search" :itemsPerPage="100">
          <template #completion="{ data }">
            {{ data.total_evaluated_participants === 0 ? "0%" : `${(data.total_evaluated_participants / data.total_participants) * 100}%` }}
          </template>
          <template #actions="actionsProps">
            <Button :label="$t('app.company.archives.restoreCampaign')" class="icon icon-left primary sm" icon="bi-arrow-counterclockwise" :loading="state.btnIsLoading" @click="restore(actionsProps.row.campaign)" />
          </template>
          <template #empty>
            <Empty :message="$t('app.company.archives.noArchivedCampaigns')" :dark="isDark" icon="folder" />
          </template>
        </Table>
      </div>
    <ActionModal modal="restore" :data="state.selectedCampaign" :isOpen="state.showActionModal" @close="state.showActionModal = false" @confirm="state.showActionModal = false" />
  </div>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { useRoute } from "vue-router";
import { useDark } from "@vueuse/core";

import Empty from "@/components/table/Empty.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import Table from "@/components/global/Table.vue";

import { useArchivedCampaigns } from "@/queries/campaigns.query";
import { Campaign } from "@/types/campaign";

const isDark = useDark();
const route = useRoute();
const { t } = useI18n();

const { data: archivedCampaigns, isPending } = useArchivedCampaigns();

const state = reactive({
  btnIsLoading: false,
  showActionModal: false,
  selectedCampaign: {} as Campaign,
  search: {
    value: "",
    searchable: ["campaign.campaign.internal_name"],
  },
  fields: [
    {
      id: "campaign.campaign.internal_name",
      label: t("app.company.archives.campaignName"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "total_participants",
      label: t("app.company.archives.totalParticipants"),
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") return a - b;
        return b - a;
      },
    },
    {
      id: "total_evaluated_participants",
      label: t("app.company.archives.evaluatedParticipants"),
      sort(a, b, dir) {
        if (dir === "ASC") return a - b;
        return b - a;
      },
    },
    {
      id: "completion",
      label: t("app.company.archives.campaignCompletion"),
      multipleSort: true,
      sort(a, b, dir) {
        const pourcA = a.total_evaluated_participants === 0 ? 0 : a.total_participants / a.total_evaluated_participants;

        const pourcB = b.total_evaluated_participants === 0 ? 0 : b.total_participants / b.total_evaluated_participants;
        if (dir === "ASC") return pourcA - pourcB;
        return pourcB - pourcA;
      },
    },
  ],
});

function restore(campaign: Campaign) {
  state.selectedCampaign = campaign;
  state.showActionModal = true;
}
</script>

<style lang="scss" scoped>

.archives-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  margin-bottom: 12px;

  .filters {
    display: flex;
    align-items: end;

    .input-wrapper {
      max-width: 350px;
      margin-left: auto;
    }
  }

  .archives-table {
    flex: 1;

    ::v-deep(table) {
      background-color: var(--white-color);
    }
  }
  
}

</style>